import React from 'react';
import { Plus, X } from 'lucide-react';
import { HelpIcon } from './FormPrimitives';

// Required roles every delivery must credit at least one person for.
// Kept in sync with backend/src/deliveryValidation.js's REQUIRED_ROLES.
export const REQUIRED_ROLES = [
  { key: 'director', label: 'Director', hint: 'The person(s) who directed the film.' },
  { key: 'writer', label: 'Writer', hint: 'The person(s) credited for the screenplay/story.' },
  { key: 'producer', label: 'Producer', hint: 'The person(s) who produced the film.' },
  { key: 'editor', label: 'Editor', hint: 'The person(s) who edited the film.' },
  { key: 'music_director', label: 'Music Director', hint: "The person(s) responsible for the film's original score/soundtrack." },
  { key: 'cast', label: 'Cast', hint: 'Actors appearing in the film — add one entry per actor.' },
];

const blankPerson = (role) => ({ role, name: '', social_handle: '', custom_role: '' });

// Guarantees at least one entry per required role — called when seeding a
// brand-new delivery form, or defensively when loading existing data that
// predates a role being added.
export function ensureRequiredRoles(list) {
  const next = Array.isArray(list) ? [...list] : [];
  for (const { key } of REQUIRED_ROLES) {
    if (!next.some(p => p.role === key)) next.push(blankPerson(key));
  }
  return next;
}

function PersonRow({ person, onChange, onRemove, showRemove, showCustomRole }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {showCustomRole && (
        <input value={person.custom_role} onChange={e => onChange({ ...person, custom_role: e.target.value })}
          placeholder="Role (e.g. DOP, Sound Designer)" style={{ flex: '1 1 160px', fontSize: 13 }} />
      )}
      <input value={person.name} onChange={e => onChange({ ...person, name: e.target.value })}
        placeholder="Full name" style={{ flex: '1 1 160px', fontSize: 13 }} />
      <input value={person.social_handle} onChange={e => onChange({ ...person, social_handle: e.target.value })}
        placeholder="Social handle (optional)" style={{ flex: '1 1 160px', fontSize: 13 }} />
      {showRemove && (
        <button type="button" onClick={onRemove} title="Remove"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0, padding: 4 }}>
          <X size={15} />
        </button>
      )}
    </div>
  );
}

// Structured cast & crew editor: one repeatable group per required role
// (Director/Writer/Producer/Editor/Music Director/Cast), plus an optional
// "Additional Crew" list for anyone else. Emits the flat entry array.
export default function CastCrewEditor({ value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const otherIndices = list.map((p, i) => (p.role === 'other' ? i : -1)).filter(i => i !== -1);

  const updateAt = (idx, person) => {
    const next = [...list];
    next[idx] = person;
    onChange(next);
  };
  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));
  const addEntry = (role) => onChange([...list, blankPerson(role)]);

  return (
    <div>
      {REQUIRED_ROLES.map(({ key, label, hint }) => {
        const indices = list.map((p, i) => (p.role === key ? i : -1)).filter(i => i !== -1);
        return (
          <div key={key} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {label} <span style={{ color: 'var(--pink)' }}>*</span>
              <HelpIcon text={hint} />
            </div>
            {indices.map(idx => (
              <PersonRow key={idx} person={list[idx]} onChange={p => updateAt(idx, p)}
                onRemove={() => removeAt(idx)} showRemove={indices.length > 1} />
            ))}
            <button type="button" onClick={() => addEntry(key)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--pink)', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '2px 0', fontFamily: 'var(--font)' }}>
              <Plus size={12} /> Add another {label.toLowerCase()}
            </button>
          </div>
        );
      })}

      <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Additional Crew (optional)
          <HelpIcon text="Anyone else worth crediting — DOP, sound designer, colorist, etc. Not required." />
        </div>
        {otherIndices.map(idx => (
          <PersonRow key={idx} person={list[idx]} onChange={p => updateAt(idx, p)}
            onRemove={() => removeAt(idx)} showRemove showCustomRole />
        ))}
        <button type="button" onClick={() => addEntry('other')}
          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--pink)', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '2px 0', fontFamily: 'var(--font)' }}>
          <Plus size={12} /> Add crew member
        </button>
      </div>
    </div>
  );
}
